import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// MUI Components
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  CircularProgress,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

// Icons
import { Bell, Menu, Search } from 'lucide-react';

// Custom hooks and utilities
import { useAuth } from '../../../hooks/contexts/AuthContext';
import { handleAmount } from '../../../shared/extra';
import { fetchUserProfile } from '../../../store/userProfile/action';

// Constants
const NAVBAR_HEIGHTS = {
  loggedIn: 56,
  guest: 40,
};

const ICON_STYLES = {
  size: 26,
  stroke: '#002865',
  strokeWidth: 1.5,
};

const ROUTES_WITHOUT_SEARCH = ['/', '/search'];

const MobileNavbar = ({ toggleDrawer, isPageTitleUpdate, pageTitle }) => {
  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isLoggedIn } = useAuth();

  // State
  const [canGoBack, setCanGoBack] = useState(false);

  // Selectors
  const userInfo = useSelector((state) => state.UserProfile?.allData);
  const userName = userInfo?.data?.name;
  const userBalance = userInfo?.data?.balance > 1 ? handleAmount(userInfo.data.balance) : '0.00';

  // Effects
  useEffect(() => {
    setCanGoBack(window.history.length > 1);

    if (!userInfo?.data?.status && isLoggedIn) {
      dispatch(fetchUserProfile());
    }
  }, [userInfo?.data?.status, isLoggedIn, dispatch]);

  // Handlers
  const handleBackClick = useCallback(() => {
    canGoBack ? navigate(-1) : navigate('/');
  }, [canGoBack, navigate]);

  // Utilities
  const capitalize = useCallback((text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }, []);

  const shouldShowSearchIcon = useMemo(() => {
    return isLoggedIn && !isPageTitleUpdate && !ROUTES_WITHOUT_SEARCH.includes(location.pathname);
  }, [isLoggedIn, isPageTitleUpdate, location.pathname]);

  // Render helpers
  const renderLeftIcon = () => {
    if (isPageTitleUpdate) {
      return (
        <IconButton
          size="medium"
          edge="start"
          aria-label="go back"
          onClick={handleBackClick}
          sx={{ color: 'primaryDark.main', p: 1.2 }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 22 }} />
        </IconButton>
      );
    }

    return (
      <IconButton
        size="medium"
        edge="start"
        aria-label="open menu"
        onClick={toggleDrawer(true)}
        sx={{ color: 'primaryDark.main', p: 1.2, mr: 1 }}
      >
        <Menu style={ICON_STYLES} />
      </IconButton>
    );
  };

  const renderCenterContent = () => {
    if (isPageTitleUpdate) {
      return (
        <Typography
          variant="h6"
          sx={{
            color: 'primaryDark.main',
            fontWeight: 600,
            fontSize: '1rem',
            textTransform: 'capitalize',
            textAlign: 'left',
          }}
        >
          {capitalize(pageTitle)}
        </Typography>
      );
    }

    if (!isLoggedIn) {
      return (
        <Typography
          variant="h6"
          sx={{
            color: 'primaryDark.main',
            fontWeight: 500,
            fontSize: '1rem',
            textTransform: 'capitalize',
          }}
        >
          Guest User
        </Typography>
      );
    }

    if (!userInfo?.data) {
      return <CircularProgress sx={{ color: 'primary.main' }} size={16} thickness={4} />;
    }

    return (
      <Stack spacing={0.2}>
        <Typography
          variant="h6"
          sx={{
            color: 'primaryDark.main',
            fontWeight: 500,
            fontSize: '0.975rem',
            textTransform: 'capitalize',
          }}
        >
          {userName}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'dark.main',
            fontSize: '0.75rem',
            lineHeight: 1.4,
          }}
        >
          Wallet: ₹{userBalance}
        </Typography>
      </Stack>
    );
  };

  const renderRightIcons = () => {
    if (!isLoggedIn || isPageTitleUpdate) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {shouldShowSearchIcon && (
          <IconButton size="medium" sx={{ p: 1 }} aria-label="search">
            <NavLink to="/search">
              <Search style={ICON_STYLES} />
            </NavLink>
          </IconButton>
        )}

        <IconButton
          size="medium"
          sx={{
            p: 1,
            background: 'rgba(255, 255, 255, 0.2)',
            WebkitBackdropFilter: 'blur(5px)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="notifications"
        >
          <NavLink to="/profile/notifications">
            <Bell style={ICON_STYLES} />
          </NavLink>
        </IconButton>
      </Box>
    );
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: '100%',
        boxShadow: 0,
        borderRadius: 0,
        backgroundColor: 'white.main',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: 2,
          py: isLoggedIn ? 1 : 0,
          minHeight: NAVBAR_HEIGHTS[isLoggedIn ? 'loggedIn' : 'guest'],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left Section */}
        <Box>{renderLeftIcon()}</Box>

        {/* Center Section */}
        <Box sx={{ flexGrow: 1, ml: 1 }}>{renderCenterContent()}</Box>

        {/* Right Section */}
        {renderRightIcons()}
      </Toolbar>
    </AppBar>
  );
};

export default MobileNavbar;
